import axios from 'axios'

// API Base URL Configuration
//const BASE_URL = 'https://gangwha-api.dvconsulting.org';
// const BASE_URL = 'https://gangwha-api-live.dvconsulting.org/ganghwa';
 const BASE_URL = 'https://api.prds.kr/ganghwa/';

// Create axios instance with default headers
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Create separate API client for memorial album endpoints (no authentication)
const memorialAlbumClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Add request interceptor for logging and authentication to main client
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    const url = config.url || '';
    
    const isPublicRead = (
      url.includes('/api/v1/goins/') ||
      url.includes('/api/v1/board/list') ||
      url.includes('/api/v1/board/detail') ||
      url.includes('/api/v1/cherish/flowers') ||
      url.includes('/api/v1/cherish/messages') ||
      url.includes('/api/v1/cherish/cherish-letter') ||
      url.includes('/api/v1/memorial-album/album/list')
    );
    
    const isWriteOperation = (
      url.includes('/api/v1/board/write') ||
      url.includes('/api/v1/board/update') ||
      url.includes('/api/v1/letter/write') ||
      url.includes('/api/v1/letter/update') ||
      url.includes('/api/v1/cherish/message') ||
      url.includes('/api/v1/memorial-album/write') ||
      url.includes('/api/v1/memorial-album/album/update') ||
      url.includes('/api/v1/cherish/write') ||
      url.includes('/api/v1/cherish/create')
    );
    
    // For memorial album write operations and cherish create operations, don't require authentication token
    // since contractor authentication modal is sufficient
    if (url.includes('/api/v1/memorial-album/write') || url.includes('/api/v1/cherish/create')) {
      // Remove any existing Authorization header for this endpoint
      if (config.headers && 'Authorization' in config.headers) {
        delete config.headers.Authorization;
      }
      return config;
    }
    
    if (token && !isPublicRead) {
      // Check if token already has "Bearer " prefix to avoid double "Bearer Bearer"
      const authHeader = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      config.headers.Authorization = authHeader;
    } else if (isPublicRead && !isWriteOperation) {
      if (config.headers && 'Authorization' in config.headers) {
        delete config.headers.Authorization;
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add request interceptor for memorial album client
memorialAlbumClient.interceptors.request.use(
  (config) => {
    // Add authentication token to requests if available
    const token = localStorage.getItem('authToken');
    if (token) {
      // Check if token already has "Bearer " prefix to avoid double "Bearer Bearer"
      const authHeader = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      config.headers.Authorization = authHeader;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle 401 unauthorized errors, but exclude certain endpoints
    if (error.response && error.response.status === 401) {
      const url = error.config?.url || '';
      // Don't redirect to login for these endpoints (public reads)
      if (!url.includes('/api/v1/goins/verify') && 
          !url.includes('/api/v1/goins/search') &&
          !url.includes('/api/v1/goins/all') &&
          !url.includes('/api/v1/goins') &&
          !url.includes('/api/v1/memorial-album/album/update') &&
          !url.includes('/api/v1/memorial-album/write') &&
          !url.includes('/api/v1/memorial-album/album/list') &&
          !url.includes('/api/v1/memorial-album/album/detail') &&
          !url.includes('/api/v1/memorial-album/album/delete') &&
          !url.includes('/api/v1/letter/write') &&
          !url.includes('/api/v1/letter/update') &&
          !url.includes('/api/v1/cherish/message') &&
          !url.includes('/api/v1/cherish/messages') &&
          !url.includes('/api/v1/cherish/flowers') &&
          !url.includes('/api/v1/cherish/cherish-letter') &&
          !url.includes('/api/v1/cherish/create') &&
          !url.includes('/api/v1/board/list') &&
          !url.includes('/api/v1/board/detail') &&
          !url.includes('/api/v1/board/write') &&
          !url.includes('/api/v1/board/update') &&
          !url.includes('/api/v1/fee/check-fee-status') &&
          !url.includes('/api/v1/fee/check-fee-status-by-customer') &&
          !url.includes('/api/v1/fee/search-by-enshrine') &&
          !url.includes('/api/v1/fee/info')) {
        // Clear stored authentication data
        localStorage.removeItem('authToken');
        localStorage.removeItem('userInfo');
        localStorage.removeItem('isLoggedIn');
        // Redirect to login page
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Add response interceptor for memorial album client (no redirects)
memorialAlbumClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // For memorial album endpoints, just reject without redirecting
    return Promise.reject(error);
  }
);

// ============================================================================
// AUTHENTICATION APIs
// ============================================================================

// Login
export const login = (nickname, password) =>
  apiClient.post('/api/v1/auth/login', { nickname, password });

// Register
export const register = (data) =>
  apiClient.post('/api/v1/auth/register', data);

// Find ID
export const findId = (name, email) =>
  apiClient.post('/api/v1/users/find-id', { name, email });

// Find Password
export const findPassword = (nickname, name, email) =>
  apiClient.post('/api/v1/users/find-password', { nickname, name, email });

// Reset Password
export const resetPassword = (userId, newPassword, confirmPassword) =>
  apiClient.post('/api/v1/users/reset-password', { userId, newPassword, confirmPassword });

// Check Duplicate ID
export const checkDuplicateId = (nickname) => {
  return apiClient.get(`/api/v1/users/check-nickname?nickname=${encodeURIComponent(nickname)}`, { 
    timeout: 10000, // 10 second timeout
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    }
  }).catch(error => {
    throw error;
  });
};

// ============================================================================
// USER MANAGEMENT APIs
// ============================================================================

// Get Places List (봉안당)
export const getPlaces = () =>
  apiClient.get('/api/v1/bongandangs/list');

// Get Current User Info
export const getCurrentUser = (userId) => {
  return apiClient.get(`/api/v1/users/detail/${userId}`);
};

// Update User Info
export const updateUser = (data) =>
  apiClient.put('/api/v1/users/update', data);

// ============================================================================
// GOINS VERIFICATION APIs
// ============================================================================

// Goins Verify API
export const verifyGoins = (contractorName, contractorBirth) => {
  console.log('Sending to API:', { customerName: contractorName, customerBirthday: contractorBirth });
  
  return apiClient.post('/api/v1/goins/verify', { 
    customerName: contractorName, 
    customerBirthday: contractorBirth
  });
};

// ============================================================================
// MEMORIAL ALBUM APIs
// ============================================================================

// New API endpoint for Bereaved Family Plaza tribute writing
export const createCherishTribute = (data) =>
  apiClient.post('/api/v1/cherish/create', {
    cherishId: data.cherishId || 0,
    goinId: data.goinId || 0,
    albumTitle: data.albumTitle || data.title || '',
    photoUrl: data.photoUrl || '',
    registIp: data.registIp || '127.0.0.1',
    registId: data.registId || 0
  });

export const createMemorialAlbum = (data) =>
  apiClient.post('/api/v1/memorial-album/write', {
    cherishId: data.cherishId || 0,
    goinId: data.goinId || 0,
    title: data.title || '',
    photoUrl: data.photoUrl || '',
    thumbUrl: data.thumbUrl || '',
    registIp: data.registIp || '127.0.0.1',
    registId: data.registId || 0
  });

export const updateMemorialAlbum = (data) =>
  memorialAlbumClient.put('/api/v1/memorial-album/album/update', {
    albumId: data.albumId,
    title: data.title,
    photoUrl: data.photoUrl,
    thumbUrl: data.thumbUrl,
    registIp: data.registIp
  });

// Profile Photo Update API - using memorial album endpoint for profile photos
export const updateProfilePhoto = (data) =>
  memorialAlbumClient.put('/api/v1/memorial-album/album/update', {
    albumId: data.albumId || 0, // Use 0 for profile photo
    title: data.title || 'Profile Photo',
    photoUrl: data.photoUrl,
    thumbUrl: data.thumbUrl || data.photoUrl, // Use same URL for thumbnail if not provided
    registIp: data.registIp || '127.0.0.1'
  });

export const getMemorialAlbumList = (cherishId) =>
  memorialAlbumClient.get(`/api/v1/memorial-album/album/list/${cherishId}`);

export const getMemorialAlbumDetail = (albumId) =>
  memorialAlbumClient.get(`/api/v1/memorial-album/album/detail/${albumId}`);

// Delete Memorial Album
export const deleteMemorialAlbum = (albumId) =>
  memorialAlbumClient.delete(`/api/v1/memorial-album/album/delete/${albumId}`);

// ============================================================================
// BOARD/MEMORIAL POST APIs
// ============================================================================

// New Board List APIs
export const getBoardList = (boardId) =>
  apiClient.get(`/api/v1/board/list?boardId=${boardId}`);

export const getBoardDetail = (id) =>
  apiClient.get(`/api/v1/board/detail/${id}`);

// Memorial Post APIs for cross-browser persistence
export const createMemorialPost = (data) =>
  apiClient.post('/api/v1/board/write', {
    boardId: data.boardId || 1, // 1 for memorial posts
    title: data.title,
    content: data.content,
    password: data.password || '',
    secretFlag: data.isPrivate ? 'Y' : 'N',
    writerName: data.author || 'Anonymous'
  });

export const updateMemorialPost = (data) =>
  apiClient.put('/api/v1/board/update', {
    contentId: data.id,
    boardId: data.boardId || 1,
    title: data.title,
    content: data.content,
    password: data.password || '',
    secretFlag: data.isPrivate ? 'Y' : 'N',
    writerName: data.author || 'Anonymous'
  });

export const deleteMemorialPost = (contentId) =>
  apiClient.delete(`/api/v1/board/delete/${contentId}`);

// ============================================================================
// CHERISH (DONATE) APIs
// ============================================================================

// New Cherish APIs for Donate Registration Data
export const getCherishMessages = () =>
  apiClient.get('/api/v1/cherish/messages');

export const getCherishFlowers = () =>
  apiClient.get('/api/v1/cherish/flowers');

// Cherish Message API (Flower Donation)
export const createCherishMessage = (data) =>
  apiClient.post('/api/v1/cherish/message', {
    cherishId: data.cherishId || 0,
    goinId: data.goinId || 0,
    flowerId: data.flowerId || 0,
    title: data.title || '',
    content: data.content || '',
    secureFlag: data.secureFlag || 'Y'
  });

// Cherish Letters by GoinId (Bereaved Family Plaza)
export const getCherishLettersByGoinId = (goinId) =>
  apiClient.get(`/api/v1/cherish/cherish-letter/${goinId}`);

// Cherish Letter Detail by LetterId (Bereaved Family Plaza)
export const getCherishLetterDetail = (letterId) =>
  apiClient.get(`/api/v1/cherish/cherish-letter/detail/${letterId}`);

// Cherish Write API (Memorial Post Writing)
export const createCherishLetter = (data) =>
  apiClient.post('/api/v1/cherish/write', {
    cherishId: data.cherishId || 0,
    goinId: data.goinId || 0,
    registId: data.registId || 0,
    letterTitle: data.letterTitle || data.title || '',
    letterContents: data.letterContents || data.content || '',
    password: data.password || ''
  });

// ============================================================================
// LETTER TO HEAVEN APIs
// ============================================================================

export const getLetterToHeavenList = ({ page = 1, size = 10, searchType, keyword } = {}) =>
  apiClient.get('/api/v1/letter/list', {
    params: {
      page,
      size,
      ...(searchType ? { searchType } : {}),
      ...(keyword ? { keyword } : {}),
    },
  });

export const getLetterToHeavenDetail = (id) =>
  apiClient.get(`/api/v1/letter/${id}`);

export const createLetterToHeaven = (data) =>
  apiClient.post('/api/v1/letter/write', {
    title: data.title,
    content: data.content,
    password: data.password,
    secureFlag: data.secureFlag ?? 'Y',
    ip: data.ip,
    userName: data.userName,
    toName: data.toName || data.recipient,
    boardStamp: data.boardStamp ?? 0,
  });

export const updateLetterToHeaven = (data) =>
  apiClient.put('/api/v1/letter/update', {
    id: data.id,
    title: data.title,
    content: data.content,
    password: data.password,
    flowerImageUrl: data.flowerImageUrl || data.boardStamp || ''
  });

export const deleteLetterToHeaven = (id, password) =>
  apiClient.delete(`/api/v1/letter/delete/${id}`, {
    data: { password }
  });

// Flower Images API
export const getFlowerImages = () =>
  apiClient.get('/api/v1/letter/flower-images');

// ============================================================================
// MANAGEMENT FEE APIs
// ============================================================================

export const searchManagementFeeByEnshrine = (enshrineName, name) => {
  return apiClient.get('/api/v1/fee/search-by-enshrine', { 
    params: { enshrineName, name } 
  });
};

export const getManagementFeeInfo = (agreementId) => {
  return apiClient.get('/api/v1/fee/info', { 
    params: { agreementId } 
  });
};

export const checkManagementFeeStatus = (deceasedName) => {
  return apiClient.get('/api/v1/fee/check-fee-status', { 
    params: { deceasedName } 
  });
};

export const checkManagementFeeStatusByCustomer = (customerName) => {
  return apiClient.get('/api/v1/fee/check-fee-status-by-customer', { 
    params: { customerName } 
  });
};

// ============================================================================
// EXPORTS
// ============================================================================

// Export apiClient for direct use
export { apiClient }; 
